import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  useApi,
  toApiError,
  type ChecklistItemInput,
  type ChecklistTemplateResponse,
} from '@/shared/api';
import { cleaningFailure } from '../cleaningFailure';

const DEFAULT_ROOMS = ['Küche', 'Bad', 'Schlafzimmer', 'Wohnzimmer', 'Flur'];

export const slotTemplateKey = (propertyId: string) =>
  ['checklistTemplates', 'byProperty', propertyId] as const;

/**
 * Die Raumliste eines Apartments; `null` heißt: noch keine angelegt. Nur der Code des Servers
 * sagt das – ein 404 von Routing oder Proxy bleibt ein Fehler, statt „keine Liste“ zu behaupten.
 */
export function useSlotTemplateQuery(propertyId: string, { enabled = true } = {}) {
  const api = useApi();
  return useQuery<ChecklistTemplateResponse | null>({
    queryKey: slotTemplateKey(propertyId),
    enabled,
    queryFn: async () => {
      try {
        return await api.checklistTemplates.getForProperty(propertyId);
      } catch (err) {
        const e = await toApiError(err);
        if (e.status === 404 && e.title === 'checklist_template_not_found') return null;
        throw err;
      }
    },
  });
}

/** Ob die Raumliste Fotos verlangt: ein Raum mit Foto genügt, ohne Liste verlangt nichts Fotos. */
export const requiresPhotos = (template: ChecklistTemplateResponse | null | undefined) =>
  Boolean(template?.items.some((item) => item.requiresPhoto));

/**
 * Lädt und speichert die Raumliste einer Property. Mit „Fotos verlangen“ wird jeder Raum ein
 * Pflichtfoto der künftigen Aufgaben; ohne bleiben die Räume gespeichert, die Aufgabe kennt dann
 * keine Fotos und wird mit „Erledigt melden“ abgeschlossen. Schon angelegte Aufgaben behalten
 * ihre Fotoliste (Schnappschuss beim Anlegen, Server).
 */
export function useSlotTemplate(propertyId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const query = useSlotTemplateQuery(propertyId);
  const data = query.data;

  const [name, setName] = useState('Standard');
  const [rooms, setRooms] = useState<string[]>(DEFAULT_ROOMS);
  const [photos, setPhotos] = useState(false);

  useEffect(() => {
    if (data) {
      setName(data.name);
      setRooms([...data.items].sort((a, b) => a.position - b.position).map((i) => i.text));
    } else if (data === null) {
      setName('Standard');
      setRooms(DEFAULT_ROOMS);
    }
    if (data !== undefined) setPhotos(requiresPhotos(data));
  }, [data]);

  const save = useMutation({
    // Ohne Netz ist Speichern gesperrt; pausieren ließe den Knopf endlos drehen.
    networkMode: 'always',
    mutationFn: () => {
      const items: ChecklistItemInput[] = rooms
        .map((text) => text.trim())
        .filter((text) => text.length > 0)
        .map((text, position) => ({ text, position, requiresPhoto: photos }));
      return data
        ? api.checklistTemplates.update(data.id, { name, items })
        : api.checklistTemplates.create({ propertyId, name, items });
    },
    onSuccess: () => {
      toast.success(
        photos
          ? 'Raumliste gespeichert – Fotos werden verlangt'
          : 'Raumliste gespeichert – ohne Fotos',
      );
    },
    onError: async (err) =>
      toast.error((await cleaningFailure(err, 'Raumliste nicht gespeichert.')).text),
    onSettled: () => qc.invalidateQueries({ queryKey: ['checklistTemplates'] }),
  });

  // Räume mit und ohne Foto gemischt (alte Daten): Speichern stellt alle gleich — die Seite sagt das.
  const mixed = Boolean(
    data?.items.some((item) => item.requiresPhoto) &&
    data.items.some((item) => !item.requiresPhoto),
  );
  return { query, name, setName, rooms, setRooms, photos, setPhotos, mixed, isCreate: !data, save };
}
